// components/AgreementHistoryTable.tsx
import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { useState, useMemo } from "react";
import { ActivePaymentAgreementCard } from "./ActivePaymentAgreementCard";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  usePaymentAgreementList,
  usePaymentAgreementDetail,
  useAccountDetails,
} from "../../hooks";
import { useInstallments } from "@features/cx/paymentsv2/hooks";
import { useParams, useNavigate } from "react-router-dom";
import { PaymentInstallment } from "../../types";
import { PageHeader } from "@shared/components/PageHeader";

export function AgreementHistoryTable() {
  const currency = useCurrency();
  const navigate = useNavigate();
  const [selectedAgreement, setSelectedAgreement] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const params = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const queryParams = {
    remoteUtilityId: remoteUtilityId || 0,
    consumerNumber: params.consumerNumber || "",
  };
  const { data: account } = useAccountDetails(queryParams);
  const remoteBillId = account?.billData?.id;
  const outstandingAmount = account?.billData?.outstandingBalance ?? 0;

  // Fetch agreement detail for active installments (paid & unpaid)
  const { data: agreementDetail } = usePaymentAgreementDetail({
    remoteUtilityId: Number(remoteUtilityId) || 0,
    remoteBillId: Number(remoteBillId) || 0,
  });

  // Fetch installments using useInstallments hook as fallback
  const { data: installmentsResponse, isLoading: isInstallmentsLoading } =
    useInstallments(
      account?.id ? Number(account.id) : undefined,
      account?.consumerNo || params.consumerNumber || "",
    );

  // Combine & map all installments (showing both Paid and Unpaid)
  const installmentList = useMemo(() => {
    const detailInstallments =
      agreementDetail?.result?.installments || agreementDetail?.installments;

    if (Array.isArray(detailInstallments) && detailInstallments.length > 0) {
      return detailInstallments.map((item: any) => ({
        id: item.id || item.installmentNo || item.installment_no,
        installmentNo:
          item.installmentNo ??
          item.installment_no ??
          item.installmentNumber ??
          "NA",
        paymentDate: item.paymentDate || item.dueDate || item.due_date || "NA",
        paidAmount:
          item.paidAmount ??
          item.installmentAmount ??
          item.installment_amount ??
          item.amount ??
          0,
        statusDisplay:
          item.statusDisplay || item.status_display || item.status || "Unpaid",
        paidDate: item.paidDate || item.paid_date || "NA",
      }));
    }

    const rawData = Array.isArray(installmentsResponse)
      ? installmentsResponse
      : installmentsResponse?.result || installmentsResponse?.results || [];

    return rawData.map((item: any) => ({
      id: item.id || item.installmentNo || item.installment_no,
      installmentNo:
        item.installmentNo ??
        item.installment_no ??
        item.installmentNumber ??
        "NA",
      paymentDate: item.paymentDate || item.dueDate || item.due_date || "NA",
      paidAmount:
        item.paidAmount ??
        item.installmentAmount ??
        item.installment_amount ??
        item.amount ??
        0,
      statusDisplay:
        item.statusDisplay || item.status_display || item.status || "Unpaid",
      paidDate: item.paidDate || item.paid_date || "NA",
    }));
  }, [agreementDetail, installmentsResponse]);

  const { data: agreementListResponse, isLoading } = usePaymentAgreementList({
    remoteUtilityId: Number(remoteUtilityId) || 0,
    remoteBillId: Number(remoteBillId) || 0,
    consumerId: account?.id ? Number(account.id) : undefined,
    page: currentPage,
    limit: PAGE_SIZE,
  });

  const totalItems = agreementListResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endItem =
    totalItems > 0 ? Math.min(currentPage * PAGE_SIZE, totalItems) : 0;

  const pagination = {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem,
    endItem,
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (agreement: any) => {
    // Map snake_case to camelCase for ActivePaymentAgreementCard
    const mappedAgreement = {
      id: agreement.id,
      agreementId: agreement.agreementNo,
      startDate: agreement.createdDate || "NA",
      frequency: agreement.installmentFrequencyDisplay || "NA",
      totalAmount: parseFloat(agreement.totalAgreementAmount) || 0,
      downPayment: parseFloat(agreement.downPayment) || 0,
      installmentAmount: parseFloat(agreement.monthlyPayment) || 0,
      remainingAmount: 0,
      paidInstallments:
        agreement.installments?.filter((i: any) => i.statusDisplay === "Paid")
          .length || 0,
      totalInstallments: agreement.noOfInstallments || 0,
      installments:
        agreement.installments?.map((i: any) => ({
          installmentNo: i.installmentNo,
          paymentDate: i.dueDate,
          paidAmount: parseFloat(i.paidAmount) || 0,
          statusDisplay: i.statusDisplay,
          paidDate: i.paidDate || "NA",
        })) || [],
    };
    setSelectedAgreement(mappedAgreement);
    setIsModalOpen(true);
  };

  // Exact Installment Schedule columns configuration from ActivePaymentAgreementCard
  const installmentColumns: TableColumn<PaymentInstallment>[] = [
    {
      key: "installmentNo",
      header: "Installment #",
      type: "text",
    },
    {
      key: "paymentDate",
      header: "Due Date",
      type: "text",
    },
    {
      key: "paidAmount",
      header: "Amount",
      type: "custom",
      render: (value: number) => (
        <span className="text-sm text-gray-900">
          {currency} {value}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Paid":
              return "default" as const;
            case "Pending":
              return "secondary" as const;
            case "Overdue":
              return "destructive" as const;
            default:
              return "outline" as const;
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Paid":
              return "bg-green-100 text-green-800";
            case "Pending":
              return "bg-yellow-100 text-yellow-800";
            case "Overdue":
              return "bg-red-100 text-red-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        },
      },
    },
    {
      key: "paidDate",
      header: "Paid Date",
      type: "custom",
      render: (value?: string) => (
        <span className="text-sm text-gray-900">{value || "NA"}</span>
      ),
    },
  ];

  // Exact Installment Schedule actions configuration from ActivePaymentAgreementCard
  const installmentActions: TableAction[] = useMemo(() => {
    return [
      {
        label: "Pay",
        onClick: (row: any) => {
          navigate("/cx/payments/create", {
            state: {
              consumerNumber: params.consumerNumber,
              paymentType: "6",
              installmentId: row.id || row.installmentNo,
            },
          });
        },
        icon: "DollarSign",
      },
    ];
  }, [params.consumerNumber, navigate]);

  const columns: TableColumn<any>[] = [
    {
      key: "agreementNo",
      header: "Agreement ID",
      type: "text",
    },
    {
      key: "createdDate",
      header: "Start Date",
      type: "text",
    },
    {
      key: "endDate",
      header: "End Date",
      type: "text",
      render: (value: any) => value || "NA",
    },
    {
      key: "installmentFrequencyDisplay",
      header: "Frequency",
      type: "text",
    },
    {
      key: "totalAgreementAmount",
      header: "Total Amount",
      type: "custom",
      render: (value: any) => (
        <span className="font-medium text-gray-900">
          {currency} {value}
        </span>
      ),
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View Details",
      onClick: (row: any) => handleViewDetails(row),
      icon: "Eye",
      variant: "outline",
    },
  ];

  return (
    <>
      <div className="space-y-8">
        {/* Installment Schedule Section (Top - exact same as ActivePaymentAgreementCard) */}
        <div>
          <PageHeader
            title="Installment Schedule"
            subtitle="View all installments and their details"
          />
          <GenericTable
            data={installmentList}
            loading={isInstallmentsLoading}
            columns={installmentColumns}
            rowKey={(row) => `installment-${row.installmentNo || row.id}`}
            actions={installmentActions}
            actionsType="buttons"
            emptyMessage="No installments found"
          />
        </div>

        {/* Agreement History Section (Bottom) */}
        <div className="space-y-4 border-t pt-6">
          <div className="flex items-center justify-between">
            <div>
              <PageHeader
                title="Agreement History"
                subtitle="View all payment agreements and their details"
              />
            </div>
          </div>

          <GenericTable
            data={
              agreementListResponse?.results ??
              agreementListResponse?.result ??
              []
            }
            loading={isLoading}
            columns={columns}
            rowKey="id"
            actions={actions}
            actionsType="icons"
            emptyMessage="No agreements found"
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Modal for Agreement Details */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 border-none shadow-none bg-transparent">
          {selectedAgreement && (
            <div className="mt-4">
              <ActivePaymentAgreementCard
                agreementId={selectedAgreement.agreementId}
                startDate={selectedAgreement.startDate}
                frequency={selectedAgreement.frequency}
                totalAmount={selectedAgreement.totalAmount}
                downPayment={selectedAgreement.downPayment}
                installmentAmount={selectedAgreement.installmentAmount}
                remainingAmount={selectedAgreement.remainingAmount}
                paidInstallments={selectedAgreement.paidInstallments}
                totalInstallments={selectedAgreement.totalInstallments}
                installments={selectedAgreement.installments}
                showActions={false}
                totalOutstanding={outstandingAmount}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
